"use client";

import { useMemo, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const LETTER_RE = /[a-z]/;
const BASE_VOWELS = ["a", "e", "i", "o", "u"] as const;

interface VowelStats {
  vowels: number;
  consonants: number;
  letters: number;
  perVowel: Map<string, number>;
}

function countVowels(text: string, includeY: boolean): VowelStats {
  const vowelSet = new Set<string>(BASE_VOWELS);
  if (includeY) {
    vowelSet.add("y");
  }
  const perVowel = new Map<string, number>();
  for (const key of vowelSet) {
    perVowel.set(key, 0);
  }
  let vowels = 0;
  let consonants = 0;
  for (const ch of text.toLowerCase()) {
    if (!LETTER_RE.test(ch)) {
      continue;
    }
    if (vowelSet.has(ch)) {
      vowels += 1;
      perVowel.set(ch, (perVowel.get(ch) ?? 0) + 1);
    } else {
      consonants += 1;
    }
  }
  return { vowels, consonants, letters: vowels + consonants, perVowel };
}

function percentOf(part: number, whole: number, fmt: NumberFormatter): string {
  if (whole === 0) {
    return "0%";
  }
  return `${fmt((part / whole) * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const VOWEL_COUNTER_STRINGS = {
  options: "Options",
  countYAsVowel: "Count Y as a vowel",
  yourText: "Your text",
  placeholder: "Paste or type your text here…",
  vowels: "Vowels",
  consonants: "Consonants",
  vowelShare: "Vowel share",
  consonantShare: "Consonant share",
  letters: "Letters",
  characters: "Characters",
  perVowelBreakdown: "Per-vowel breakdown",
  ofVowels: "of vowels",
} as const;

export default function VowelCounterWidget() {
  const strings = useToolUi(VOWEL_COUNTER_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [includeY, setIncludeY] = useState(false);

  const stats = useMemo(() => countVowels(input, includeY), [input, includeY]);
  const vowelOrder = includeY ? [...BASE_VOWELS, "y"] : [...BASE_VOWELS];

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
          <input
            checked={includeY}
            className="accent-primary"
            onChange={(e) => setIncludeY(e.target.checked)}
            type="checkbox"
          />
          {strings.countYAsVowel}
        </label>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="vowel-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="vowel-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label={strings.vowels} value={stats.vowels} />
        <StatCard label={strings.consonants} value={stats.consonants} />
        <StatCard
          label={strings.vowelShare}
          value={percentOf(stats.vowels, stats.letters, fmt)}
        />
        <StatCard
          label={strings.consonantShare}
          value={percentOf(stats.consonants, stats.letters, fmt)}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
        <StatCard label={strings.letters} value={stats.letters} />
        <StatCard label={strings.characters} value={input.length} />
      </div>

      {/* Per-vowel breakdown */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.perVowelBreakdown}
        </h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
          {vowelOrder.map((vowel) => {
            const count = stats.perVowel.get(vowel) ?? 0;
            return (
              <div
                className="rounded-lg border border-border bg-card p-4 text-center"
                key={vowel}
              >
                <p className="font-mono font-semibold text-2xl text-foreground uppercase">
                  {vowel}
                </p>
                <p className="mt-1 font-medium text-foreground text-lg">
                  {count}
                </p>
                <p className="mt-1 text-muted-foreground text-xs">
                  {percentOf(count, stats.vowels, fmt)} {strings.ofVowels}
                </p>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
